import { useState, useEffect, useMemo } from 'react';
import Security from '../security/Security.js';

/**
 * Visibilidad de columnas para las grillas de React/MUI.
 *
 * Las grillas de DataTables traen esto incluido (el botón `colvis` de Buttons, ver
 * docs/design-system/grid-column-selector.md). Las de MUI no, y hoy cada una resuelve a
 * mano un `columnsEnabled` que en realidad es otra cosa: qué columnas habilita la CONFIG
 * del cliente, no cuáles eligió ver el usuario. Este hook aporta la segunda mitad.
 *
 * DOS EJES DISTINTOS, y conviene no confundirlos:
 *  - `enabled`  -> la columna existe para este cliente (flag de config, permiso). Si está
 *                 en false la columna no se ve NI aparece en el selector: para ese cliente
 *                 no existe.
 *  - visibilidad -> de las que existen, cuáles decidió ver el usuario. Es lo que persiste
 *                 este hook y lo que cambia el selector.
 *
 * PERSISTE POR NOMBRE, NO POR ÍNDICE. Es la diferencia deliberada con el `colvis` de
 * DataTables, que guarda un array posicional: al prender o apagar un flag de config los
 * índices se corren y la visibilidad restaurada cae en la columna equivocada. Guardar los
 * nombres de las ocultas hace que un cambio de flags sea inofensivo, y de paso que lo
 * guardado se pueda leer.
 *
 * La lógica va en funciones puras exportadas y el hook es la cáscara con el estado: así se
 * puede testear sin un renderer de componentes, que este proyecto no tiene.
 */

/** Las que el selector puede listar: existen para este cliente y no son fijas. */
export const columnasSeleccionables = (columnas) =>
	(columnas || []).filter((c) => c && c.enabled !== false && c.fixed !== true);

/**
 * Una columna fija se ve siempre, aunque una entrada vieja de localStorage la nombre: que
 * se pueda ocultar la columna de acciones deja la fila sin sus botones. Una columna que no
 * está declarada también se ve — es más seguro mostrar de más que esconder una columna que
 * nadie puede volver a habilitar.
 */
export const columnaVisible = (columnas, ocultas, name) => {
	const columna = (columnas || []).find((c) => c && c.name === name);
	if (!columna) return true;
	if (columna.enabled === false) return false;
	if (columna.fixed === true) return true;
	return (ocultas || []).indexOf(name) === -1;
};

/**
 * El mapa `{ nombre: boolean }` que consumen las celdas. Combina los dos ejes en un solo
 * valor, así una celda pregunta una sola cosa y las filas no necesitan saber que existe
 * este hook: siguen recibiendo el mismo objeto que antes.
 */
export const mapaDeVisibles = (columnas, ocultas) => {
	const visibles = {};
	(columnas || []).forEach((c) => {
		if (c && c.name) visibles[c.name] = columnaVisible(columnas, ocultas, c.name);
	});
	return visibles;
};

/** Alterna un nombre en la lista de ocultas, sin mutarla. */
export const alternarOculta = (ocultas, name) => {
	const lista = ocultas || [];
	return lista.indexOf(name) === -1 ? lista.concat([name]) : lista.filter((n) => n !== name);
};

/** Lo persistido, tolerando basura: si no es una lista de nombres, no hay nada oculto. */
export const leerOcultasPersistidas = (storageKey) => {
	if (!storageKey) return [];
	try {
		const guardado = JSON.parse(localStorage.getItem(storageKey));
		return Array.isArray(guardado) ? guardado : [];
	} catch (e) {
		return [];
	}
};

/**
 * @param columns    [{ name, messageId, label, enabled, fixed }]
 *                   `fixed`: no se puede ocultar y no aparece en el selector (la columna de
 *                   acciones, los iconos sin título). Equivale al `:not(.no-colvis):not(:empty)`
 *                   de las grillas de DataTables.
 * @param storageKey key de localStorage. Sin ella no persiste. Se acota al usuario logueado
 *                   (VEC-3769): en un dispositivo compartido, las columnas que ocultó uno no
 *                   son las del que entra después.
 */
const useColumnVisibility = (columns, storageKey) => {
	const columnas = columns || [];
	// `migrarClaveSinUsuario` es TEMPORAL (ver Security.js): adopta la entrada sin usuario de la
	// versión anterior. Mismo motivo que en GridFilterPanel para resolverla en el render.
	const clave = useMemo(() => (storageKey ? Security.migrarClaveSinUsuario(storageKey) : null), [storageKey]);
	const [ocultas, setOcultas] = useState(() => leerOcultasPersistidas(clave));

	useEffect(() => {
		if (clave) localStorage.setItem(clave, JSON.stringify(ocultas));
	}, [clave, ocultas]);

	const seleccionables = columnasSeleccionables(columnas);

	return {
		visibles: mapaDeVisibles(columnas, ocultas),
		esVisible: (name) => columnaVisible(columnas, ocultas, name),
		toggle: (name) => setOcultas((prev) => alternarOculta(prev, name)),
		mostrarTodas: () => setOcultas([]),
		seleccionables: seleccionables,
		hayOcultas: seleccionables.some((c) => ocultas.indexOf(c.name) !== -1),
	};
};

export default useColumnVisibility;
